import { useEffect, useState } from 'react'
import { adminService, type Holiday } from '../../identity/services/admin'
import { authService } from '../../identity/services/auth'
import { CalendarDays, Trash2, CheckCircle, AlertCircle } from 'lucide-react'

const DAY_LABELS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
]

export default function WorkingCalendarPage() {
  const tenantId = authService.getCurrentUser()?.tenant_id
  const [workingDays, setWorkingDays] = useState<number[]>([1, 2, 3, 4, 5, 6])
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [loading, setLoading] = useState(true)
  const [savingDays, setSavingDays] = useState(false)
  const [addingHoliday, setAddingHoliday] = useState(false)
  const [newDate, setNewDate] = useState('')
  const [newLabel, setNewLabel] = useState('')
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (!tenantId) return
    adminService.getWorkingCalendar(tenantId)
      .then(cal => { setWorkingDays(cal.working_days); setHolidays(cal.holidays) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [tenantId])

  const toggleDay = (day: number) => {
    setWorkingDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort())
  }

  const saveDays = async () => {
    if (!tenantId) return
    setSavingDays(true)
    setMsg(null)
    try {
      const cal = await adminService.updateWorkingDays(tenantId, workingDays)
      setWorkingDays(cal.working_days)
      setMsg({ type: 'success', text: 'Working days saved.' })
    } catch {
      setMsg({ type: 'error', text: 'Failed to save working days.' })
    } finally {
      setSavingDays(false)
    }
  }

  const addHoliday = async () => {
    if (!tenantId || !newDate) return
    setAddingHoliday(true)
    setMsg(null)
    try {
      const cal = await adminService.addHoliday(tenantId, { date: newDate, label: newLabel })
      setHolidays(cal.holidays)
      setNewDate('')
      setNewLabel('')
    } catch {
      setMsg({ type: 'error', text: 'Failed to add holiday.' })
    } finally {
      setAddingHoliday(false)
    }
  }

  const removeHoliday = async (date: string) => {
    if (!tenantId) return
    try {
      const cal = await adminService.removeHoliday(tenantId, date)
      setHolidays(cal.holidays)
    } catch {
      setMsg({ type: 'error', text: 'Failed to remove holiday.' })
    }
  }

  if (loading) return <div className="spinner" />

  return (
    <div className="space-y-6" style={{ maxWidth: 640 }}>
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <CalendarDays className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            Working Calendar
          </h1>
          <p className="page-subtitle">
            Business days and holidays — drives which callback time slots customers can pick on WhatsApp.
          </p>
        </div>
      </div>

      {msg && (
        <div className={msg.type === 'success' ? 'alert-success' : 'alert-danger'}>
          {msg.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="card">
        <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Working days</h2>
        <div className="flex gap-2 flex-wrap mb-4">
          {DAY_LABELS.map(d => {
            const active = workingDays.includes(d.value)
            return (
              <button
                key={d.value}
                type="button"
                className={active ? 'badge-info' : 'badge-neutral'}
                style={{ padding: '8px 14px', cursor: 'pointer', border: 'none' }}
                onClick={() => toggleDay(d.value)}
              >
                {d.label}
              </button>
            )
          })}
        </div>
        <button className="btn-primary" onClick={saveDays} disabled={savingDays}>
          {savingDays ? 'Saving…' : 'Save Working Days'}
        </button>
      </div>

      <div className="card">
        <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Holidays</h2>
        <div className="flex gap-2 items-end mb-4 flex-wrap">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date</label>
            <input type="date" className="input" value={newDate} onChange={e => setNewDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 160 }}>
            <label className="form-label">Label</label>
            <input
              type="text" className="input" value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="e.g. Diwali"
            />
          </div>
          <button className="btn-secondary" onClick={addHoliday} disabled={addingHoliday || !newDate}>
            {addingHoliday ? 'Adding…' : 'Add Holiday'}
          </button>
        </div>

        {holidays.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No holidays configured.</p>
        ) : (
          <div className="space-y-2">
            {holidays.map(h => (
              <div key={h.date} className="flex items-center justify-between" style={{ borderBottom: '0.5px solid var(--border)', paddingBottom: 8 }}>
                <div>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                    {new Date(h.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                  </span>
                  {h.label && <span className="ml-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>{h.label}</span>}
                </div>
                <button className="btn-icon" onClick={() => removeHoliday(h.date)} title="Remove holiday">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
